import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_objectif-ecosystem/artifacts/4tz74flx_LOGO%20OBJECTIF%20SCOP%20OUTREMER%202%20%283%29.png';
const BG_URL = 'https://images.unsplash.com/photo-1748810859309-b943147cdda2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxDYXJpYmJlYW4lMjBpc2xhbmQlMjBwYXJhZGlzZSUyMGFlcmlhbCUyMGx1c2glMjBncmVlbiUyMHRyb3BpY2FsJTIwY29hc3RsaW5lfGVufDB8fHx8MTc3NTI3MzAzNnww&ixlib=rb-4.1.0&q=85';

export default function ClientLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/client/auth/login`, { email, password }, { withCredentials: true });
      navigate('/espace-client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${BG_URL}')` }} />
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(160deg, rgba(26,36,33,0.85) 0%, rgba(45,90,76,0.72) 50%, rgba(0,174,239,0.35) 100%)'
      }} />
      <div className="absolute top-10 right-14 w-28 h-28 rounded-full opacity-20" style={{ background: 'radial-gradient(circle, #80C51F, transparent)' }} />
      <div className="absolute bottom-16 left-12 w-20 h-20 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #00AEEF, transparent)' }} />

      <div className="relative z-10 w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={LOGO_URL} alt="Objectif SCOP Outremer" className="w-20 h-20 mx-auto object-contain drop-shadow-2xl mb-4" style={{ filter: 'drop-shadow(0 0 20px rgba(0,174,239,0.3))' }} data-testid="client-login-logo" />
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Espace Client</h1>
          <p className="text-white/70 mt-1 text-sm">Objectif SCOP Outremer</p>
          <div className="flex gap-1 mt-3 justify-center">
            <div className="h-0.5 w-6 rounded-full" style={{ background: '#00AEEF' }} />
            <div className="h-0.5 w-6 rounded-full" style={{ background: '#80C51F' }} />
            <div className="h-0.5 w-6 rounded-full" style={{ background: '#EA3C3A' }} />
            <div className="h-0.5 w-6 rounded-full" style={{ background: '#FF8E00' }} />
            <div className="h-0.5 w-6 rounded-full" style={{ background: '#D5007D' }} />
          </div>
        </div>

        <Card className="border-white/10 shadow-2xl bg-white/95 backdrop-blur-md">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client-email" className="text-[#1A2421]">Adresse email</Label>
                <Input id="client-email" type="email" placeholder="votre@entreprise.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20" data-testid="client-login-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client-password" className="text-[#1A2421]">Mot de passe</Label>
                <div className="relative">
                  <Input id="client-password" type={showPassword ? 'text' : 'password'} placeholder="Votre mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-[#E8E6E1] focus:border-[#2D5A4C] focus:ring-[#2D5A4C]/20 pr-10" data-testid="client-login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5E6D66] hover:text-[#1A2421] transition-colors" tabIndex={-1} data-testid="toggle-client-password">
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#00AEEF] hover:bg-[#0095cc]" disabled={loading} data-testid="client-login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Acceder a mon espace
              </Button>
            </form>
            <div className="mt-6 pt-4 border-t border-[#E8E6E1] text-center space-y-1">
              <p className="text-xs text-[#5E6D66]">Portail reserve aux organisations partenaires.</p>
              <div className="flex justify-center gap-4">
                <a href="/portail" className="text-xs text-[#2D5A4C] hover:underline">Portail Membre FTPE</a>
                <a href="/login" className="text-xs text-[#5E6D66] hover:underline">Equipe interne</a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
